// Lightweight, dependency-free inline-SVG charts for the admin dashboard.
// Brand orange is the magnitude hue; status colors are reserved + always
// paired with a text label, never color-alone.

const ORANGE = "#fc8019";
const ORANGE_SOFT = "rgba(252,128,25,0.14)";
const AXIS = "rgba(120,113,108,0.35)";

export const STATUS_COLORS = {
  paid: "#15803d",
  pending: "#f59e0b",
  processing: "#f59e0b",
  shipped: "#2563eb",
  delivered: "#15803d",
  cancelled: "#dc2626",
  failed: "#dc2626",
  cod: "#2563eb",
  other: "#78716c",
  returning: "#2fb56b",
  "one-time": "#7c6cf0",
};

function statusColor(key) {
  return STATUS_COLORS[String(key || "other").toLowerCase()] || STATUS_COLORS.other;
}

// Bars over time (e.g. orders per day). data: [{ label, value }]
export function BarTrend({ data, height = 160, format = (v) => v, title }) {
  const w = 640;
  const pad = { top: 16, right: 12, bottom: 26, left: 34 };
  const innerW = w - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...data.map((d) => d.value));
  const step = innerW / Math.max(1, data.length);
  const barW = Math.max(4, Math.min(28, step * 0.6));
  const ticks = [0, Math.round(max / 2), max];

  return (
    <svg className="rzp-chart" viewBox={`0 0 ${w} ${height}`} role="img" aria-label={title || "Bar chart"}>
      {ticks.map((t, i) => {
        const y = pad.top + innerH - (t / max) * innerH;
        return (
          <g key={i}>
            <line x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke={AXIS} strokeWidth="1" strokeDasharray={i === 0 ? "0" : "3 3"} />
            <text x={pad.left - 6} y={y + 3} textAnchor="end" className="rzp-chart-tick">{format(t)}</text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const h = (d.value / max) * innerH;
        const x = pad.left + i * step + (step - barW) / 2;
        const y = pad.top + innerH - h;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={Math.max(0, h)} rx="4" fill={ORANGE}>
              <title>{`${d.label}: ${format(d.value)}`}</title>
            </rect>
            {data.length <= 16 && (
              <text x={x + barW / 2} y={height - 10} textAnchor="middle" className="rzp-chart-xlabel">{d.label}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// Area + line over time. data: [{ label, value }]
export function AreaTrend({ data, height = 160, format = (v) => v, title }) {
  const w = 640;
  const pad = { top: 16, right: 12, bottom: 26, left: 44 };
  const innerW = w - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...data.map((d) => d.value));
  const n = Math.max(1, data.length - 1);
  const px = (i) => pad.left + (i / n) * innerW;
  const py = (val) => pad.top + innerH - (val / max) * innerH;
  const line = data.map((d, i) => `${i === 0 ? "M" : "L"} ${px(i)} ${py(d.value)}`).join(" ");
  const area = `${line} L ${px(data.length - 1)} ${pad.top + innerH} L ${px(0)} ${pad.top + innerH} Z`;
  const ticks = [0, Math.round(max / 2), max];

  return (
    <svg className="rzp-chart" viewBox={`0 0 ${w} ${height}`} role="img" aria-label={title || "Trend chart"}>
      {ticks.map((t, i) => {
        const y = py(t);
        return (
          <g key={i}>
            <line x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke={AXIS} strokeWidth="1" strokeDasharray={i === 0 ? "0" : "3 3"} />
            <text x={pad.left - 6} y={y + 3} textAnchor="end" className="rzp-chart-tick">{format(t)}</text>
          </g>
        );
      })}
      <path d={area} fill={ORANGE_SOFT} />
      <path d={line} fill="none" stroke={ORANGE} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {data.map((d, i) => (
        <circle key={i} cx={px(i)} cy={py(d.value)} r="3" fill={ORANGE}>
          <title>{`${d.label}: ${format(d.value)}`}</title>
        </circle>
      ))}
      {data.map((d, i) =>
        (data.length <= 16 || i % Math.ceil(data.length / 8) === 0) ? (
          <text key={`x${i}`} x={px(i)} y={height - 8} textAnchor="middle" className="rzp-chart-xlabel">{d.label}</text>
        ) : null
      )}
    </svg>
  );
}

// Horizontal magnitude bars (e.g. top locations). data: [{ label, value, sub }]
export function HBars({ data, format = (v) => v }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  if (!data.length) return <p className="rzp-empty">No data yet.</p>;
  return (
    <div className="rzp-hbars">
      {data.map((d, i) => (
        <div className="rzp-hbar" key={i}>
          <span className="rzp-hbar-label" title={d.label}>{d.label}</span>
          <span className="rzp-hbar-track">
            <span className="rzp-hbar-fill" style={{ width: `${(d.value / max) * 100}%` }} />
          </span>
          <span className="rzp-hbar-value">{format(d.value)}</span>
        </div>
      ))}
    </div>
  );
}

// Donut for a labelled breakdown (order status, returning vs new, etc).
// data: [{ label, value }]
export function StatusDonut({ data, size = 150, unitLabel = "orders", title = "Status breakdown", emptyLabel = "No orders yet." }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const r = size / 2 - 10;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  if (!total) return <p className="rzp-empty">{emptyLabel}</p>;

  return (
    <div className="rzp-donut-wrap">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={title}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(120,113,108,0.15)" strokeWidth="14" />
        {data.map((d, i) => {
          const frac = d.value / total;
          const dash = frac * circ;
          const seg = (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={statusColor(d.label)}
              strokeWidth="14"
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${cx} ${cy})`}
            >
              <title>{`${d.label}: ${d.value}`}</title>
            </circle>
          );
          offset += dash;
          return seg;
        })}
        <text x={cx} y={cy - 2} textAnchor="middle" className="rzp-donut-total">{total}</text>
        <text x={cx} y={cy + 16} textAnchor="middle" className="rzp-donut-sub">{unitLabel}</text>
      </svg>
      <ul className="rzp-legend">
        {data.map((d, i) => (
          <li key={i}>
            <span className="rzp-legend-dot" style={{ background: statusColor(d.label) }} />
            <span className="rzp-legend-label">{d.label}</span>
            <span className="rzp-legend-val">{d.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
